import axios from 'axios';
import AccordiumService from './AccordiumService';
import http from './Request';
import httpWithRetry from './RequestWithRetry';
import CmsAPI from '../../common/apis/CmsAPI';
import VormatsAPI from '../../common/apis/VormatsAPI';
import { Amplify, Auth, Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import configs from '../../../conf';
import ResourceHelper from '../utils/ResourceHelper';
import SubtitlesHelper from '../utils/SubtitlesHelper';

const config = configs[process.env.APP_CONF];

const getUrlParams = params => {
  const urlParams = new URLSearchParams();
  if (params.page) {
    urlParams.append('page', params.page);
  }
  if (params.size) {
    urlParams.append('size', params.size);
  }
  if (params.searchTerm) {
    urlParams.append('searchTerm', params.searchTerm);
  }

  if (params.omitFiledStories) {
    urlParams.append('omitFiledStories', params.omitFiledStories);
  }

  if (params.sort) params.sort.forEach(column => urlParams.append('sort', column));

  return urlParams;
};

export default class VideoService extends AccordiumService {
  /**
   * @typedef {Object} Page
   * @property {Object[]} content List of documents
   * @property {number} totalElements
   * @property {number} totalPages
   * @property {number} size
   * @property {number} page
   */

  /**
   * Get a paginated list of VIDEOS contained in a Page object
   * @param {Object} params - Object containing request parameters
   * @param {Number} [params.page] - page number, default is 0
   * @param {Number} [params.size] - page size, default is 20
   * @param {String} [params.searchTerm] - search term
   * @param {String[]} [params.sort] - columns to sort by, formatted as {columnName,desc} for reverse ordering
   * @returns {Promise<Page>}
   */
  static async getVideos(params) {
    //const urlParams = getUrlParams(params);  // We will re-implement filter/search params once implemented in the CMS
    let videos = await CmsAPI.getStoriesV2(params);
    return videos;
  }

  static async getVideoDetails(storyId, scenesType = null) {
    let video = await CmsAPI.getStory(storyId, scenesType);
    return video.response ? video.response.status : video.result;
  }

  static async downloadStory(storyId, onSuccess = null, onError = null, tries = 1, params = null, isDownloadingAllResources = false) {
    try {
      let response = await CmsAPI.downloadStory(storyId, params);

      // Retry on 404
      if ('status' in response && (response.status === 404 || response.status.toLowerCase() === 'processing') && tries <= 100) {
        console.log(`${response.status}, probing again in ${3 * tries}s`);
        setTimeout(function () {
          VideoService.downloadStory(storyId, onSuccess, onError, tries + 1, params, isDownloadingAllResources);
        }, 3000 * tries);
        return null;
      }

      if (response.status.toLowerCase() == 'succeeded') {
        console.log('Succeeded');

        if (onSuccess !== null) {
          onSuccess(response, isDownloadingAllResources);
        }

        return true;
      }

      if (response.status.toLowerCase() == 'failed') {
        if (onError !== null) {
          onError(response);
        }

        return false;
      }
    } catch (e) {
      console.log(e);
    }
  }

  static async duplicateVideo(storyId) {
    let duplicateVideoRes = await CmsAPI.duplicateStory(storyId);
    return duplicateVideoRes.result;
  }

  static async duplicateScene(sceneId, storyId) {
    const result = await CmsAPI.duplicateScene(sceneId, { storyId });
    return result;
  }

  static async createScene(scene) {
    const result = await CmsAPI.createScene(scene);
    return result;
  }

  static async getProtectedVideo(storyId, password, isAuth) {
    let video = await CmsAPI.getProtectedStory(storyId, password, isAuth);

    console.log('videoRes', video);

    return video.response ? video.response.status : video.result;
  }

  static async updateVideo(storyId, story, runMusicJob) {
    let response = await CmsAPI.updateStory(storyId, story, runMusicJob);

    console.log(response);

    return response;
  }

  static async batchUpdateVideos(payload) {
    let response = await CmsAPI.batchUpdateStory(payload);

    return response;
  }

  static async updateScene(sceneId, scene) {
    let response = await CmsAPI.updateScene(sceneId, scene);

    console.log(response);

    return response;
  }

  static async updateVideoReviewStatus(storyId, story) {
    let response = await CmsAPI.updateStoryReviewStatus(storyId, story);

    return response;
  }
  static async getStoryStatus(storyId) {
    let response = await CmsAPI.getStoryStatus(storyId);

    return response;
  }

  static async claimVideo(storyId, folder) {
    let response = await CmsAPI.claimStory(storyId, folder);

    return response;
  }

  static async deleteVideoForever(storyId) {
    let response = await CmsAPI.deleteStory(storyId, true);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async deleteScene(sceneId) {
    let response = await CmsAPI.deleteScene(sceneId, true);
    return response;
  }

  /**
   * Generates one or more static or animated thumbnails using the longrunning jobs API
   *
   * @param {string} story
   * @param {string} label The label to identify this thumbnail by, no spaces or special characters
   * @param {string} filename Filename including extension. If .gif, an animated thumbnail will be generated
   * @param {int} timestamp In seconds (optional)
   * @param {object} relatedTo If you want to relate this thumbnail to a story or scene, e.g. { model: "story", id: "3f77f296-3d04-4eca-aee3-ecf8f12f2ac3"} (optional)
   * @param {object} properties The properties for the thumbnail
   * @returns API response, which is the Job ID in case of success
   */
  static async generateThumbnail(story, label, filename, timestamp = 2.0, relatedTo = null, properties = null) {
    let outputs = {};

    if (properties == null) {
      properties = {
        custom: true,
        animated: filename.endsWith('gif'),
        selected: false,
      };
    }

    outputs[`${label}`] = {
      type: 'thumbnail',
      path: `thumbnails/${story.id}/${filename}`,
      offset: timestamp,
      relatedTo: relatedTo,
      properties: properties,
    };

    let config = {
      input: decodeURIComponent(story.video),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  /**
   * Generates one or more static or animated thumbnails using the longrunning jobs API
   *
   * @param {string} story
   * @param {array} thumbnails Array of objects, where each object is a thumbnail, and specifies a label, filename, and optionally a timestamp
   * @param {object} relatedTo If you want to relate this thumbnail to a story or scene, e.g. { model: "story", id: "3f77f296-3d04-4eca-aee3-ecf8f12f2ac3"} (optional)
   *
   * @param {string} label The label to identify this thumbnail by, no spaces or special characters
   * @param {string} filename Filename including extension. If .gif, an animated thumbnail will be generated
   * @param {int} timestamp In seconds (optional)
   *
   * @returns API response, which is the Job ID in case of success
   */
  static async generateThumbnails(story, thumbnails, relatedTo = null) {
    let outputs = {};

    thumbnails.forEach(function (thumbnail) {
      let properties = {
        custom: true,
        animated: thumbnail.filename.endsWith('gif'),
        selected: false,
      };

      outputs[`${thumbnail.label}`] = {
        type: 'thumbnail',
        path: `thumbnails/${story.id}/${thumbnail.filename}`,
        offset: thumbnail.timestamp ? thumbnail.timestamp : 2.0,
        relatedTo: relatedTo,
        properties: properties,
      };
    });

    if (!story.video) {
      return false;
    }

    let config = {
      input: decodeURIComponent(story.video),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async generateWaveform(story, relatedTo = null) {
    let outputs = {};

    // By default, relate the waveform to the story
    if (relatedTo == null) {
      relatedTo = {
        model: 'story',
        id: story.id,
      };
    }

    outputs['waveform'] = {
      type: 'waveform',
      path: `waveforms/${story.id}/waveform.json`,
      relatedTo: relatedTo,
      properties: null,
    };

    let config = {
      input: decodeURIComponent(story.video),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async generateHls(scene) {
    let outputs = {};

    outputs['hls'] = {
      type: 'hls',
      path: `hls/scenes/${scene.id}/stream.m3u8`,
      relatedTo: {
        model: 'scene',
        id: scene.id,
      },
      properties: null,
    };

    let config = {
      input: decodeURIComponent(scene.video),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async generateResources(scene, generateThumbnail = false, generateHls = false, generateWaveform = false) {
    let outputs = {};
    let relatedTo = {
      model: 'scene',
      id: scene.id,
    };

    if (generateThumbnail) {
      outputs[`defaultStaticThumbnail`] = {
        type: 'thumbnail',
        path: `thumbnails/${scene.id}/${scene.id}.jpg`,
        offset: 2.0,
        relatedTo: relatedTo,
        properties: {
          custom: false,
          animated: false,
          selected: false,
        },
      };
    }

    if (generateHls) {
      outputs['hls'] = {
        type: 'hls',
        path: `hls/scenes/${scene.id}/stream.m3u8`,
        relatedTo: relatedTo,
        properties: null,
      };
    }

    if (generateWaveform) {
      outputs['waveform'] = {
        type: 'waveform',
        path: `waveforms/${scene.id}/waveform.json`,
        relatedTo: relatedTo,
        properties: null,
      };
    }

    let config = {
      input: decodeURIComponent(scene.originalVideo !== undefined ? scene.originalVideo : scene.video),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async checkJobs(storyId, sceneId = null) {
    const response = await CmsAPI.checkJobs(storyId, sceneId);
    return response;
  }

  static async checkStoryStatus(storyId) {
    const response = await CmsAPI.checkStoryStatus(storyId);
    return response;
  }

  static async burnSubtitles(story) {
    let outputs = {};

    let subtitles = SubtitlesHelper.getSubtitlesFromMetadata(story.subtitlesMeta, true, true);

    if (subtitles === undefined) {
      console.error('No subtitles found');
      return false;
    }

    outputs['storyWithHardsubs'] = {
      type: 'hardsubs',
      path: `video/${story.id}/${story.id}_hardsubs.mp4`,
      relatedTo: {
        model: 'story',
        id: story.id,
      },
      properties: null,
      srtPath: subtitles.srtPath,
    };

    let inputUrl = story.video;

    if (story.source == 'web') {
      let trimmedStory = ResourceHelper.getResourceByLabel(story, 'trimmedStory');

      if (!trimmedStory) {
        return false;
      }

      inputUrl = trimmedStory.url;
    }

    let config = {
      input: decodeURIComponent(inputUrl),
      outputs: outputs,
    };

    let response = await CmsAPI.addJob(config);

    console.log(response);

    return 'id' in response ? response.id : response;
  }

  static async getAppConfig(username) {
    let appConfig = await CmsAPI.getAppConfig(username);

    console.log('appConfig', typeof appConfig === 'object' ? appConfig : undefined);

    return appConfig.result;
  }

  static getRecentVideos() {
    return http.get(`${process.env.WEB_API}/videos/recent`, VideoService.getHeaders()).then(response => response.data);
  }

  static emptyTrash() {
    return http.delete(`${process.env.WEB_API}/videos/trashed`, VideoService.getHeaders()).then(response => response.data);
  }

  static getArchivedVideos(params) {
    const urlParams = getUrlParams(params);
    return http
      .get(`${process.env.WEB_API}/videos/archived/?${urlParams.toString()}`, VideoService.getHeaders())
      .then(response => response.data);
  }

  static getVideoCount() {
    return VideoService.getVideos({ size: 1 }).then(page => page.totalElements);
  }

  static getVideoURL(uuid) {
    return httpWithRetry.get(`${process.env.WEB_API}/videos/${uuid}/url`, VideoService.getHeaders()).then(response => response.data);
  }

  static getShareURL(uuid) {
    return `${config.domain}/engage/${uuid}`;
  }

  static getVideoAlternateURL(uuid) {
    return httpWithRetry
      .get(`${process.env.WEB_API}/videos/${uuid}/alternateurl`, VideoService.getHeaders())
      .then(response => response.data);
  }

  static getCancelTokenSource() {
    return axios.CancelToken.source();
  }

  static uploadVideo(videoName, videoBlob, onProgress, cancelTokenSource) {
    const axiosConfig = VideoService.getMultipartHeaders();
    axiosConfig.cancelToken = cancelTokenSource.token;
    if (onProgress) {
      axiosConfig.onUploadProgress = onProgress;
    }
    const formData = new FormData();
    formData.append(videoName, videoBlob);
    return http.post(`${process.env.WEB_API}/videos/upload`, formData, axiosConfig).then(response => response.data);
  }

  static deactivateVideo(uuid) {
    return http
      .post(`${process.env.WEB_API}/videos/${uuid}/deactivate`, null, VideoService.getHeaders())
      .then(response => response.data);
  }

  static activateVideo(uuid) {
    return http
      .post(`${process.env.WEB_API}/videos/${uuid}/activate`, null, VideoService.getHeaders())
      .then(response => response.data);
  }

  static archiveVideo(uuid) {
    return http.post(`${process.env.WEB_API}/videos/${uuid}/archive`, null, VideoService.getHeaders()).then(response => response.data);
  }

  static unarchiveVideo(uuid) {
    return http
      .post(`${process.env.WEB_API}/videos/${uuid}/unarchive`, null, VideoService.getHeaders())
      .then(response => response.data);
  }

  static unarchiveVideoSetProgress(uuid, setProgress) {
    return (
      http
        .post(`${process.env.WEB_API}/videos/${uuid}/unarchive`, null, {
          headers: VideoService.getHeaders(),
          onDownloadProgress: progressEvent => {
            const precent = Math.floor((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(precent);
          },
        })
        // eslint-disable-next-line no-shadow
        .then(response => response.data)
    );
  }

  static getAnonymousRex(uuid) {
    return http.get(`${process.env.WEB_API}/videos/${uuid}/anon`, VideoService.getHeaders()).then(response => response.data);
  }

  static getVideoMessages(params) {
    const { videoId, ...rest } = params;
    const urlParams = getUrlParams(rest);
    return http
      .get(`${process.env.WEB_API}/videos/${videoId}/videomessages/?${urlParams.toString()}`, VideoService.getHeaders())
      .then(response => response.data);
  }

  static requestEmulator() {
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 1000);
    });
  }

  static async downloadVideo(videosInfo, updateDownloads = () => { }) {
    let startDate = new Date();

    const objDate = videosInfo.reduce((acc, { name }) => {
      return {
        [name]: 0,
        ...acc,
      };
    }, {});

    await Promise.all(
      videosInfo.map(async ({ name, url }) => {
        const type = url
          .split(/[#?]/)[0]
          .split('.')
          .pop()
          .trim();

        await http
          .get(`${url}`, {
            headers: {
              'Content-Type': `video/${type}`,
            },
            responseType: 'blob',
            onDownloadProgress: progressEvent => {
              objDate[name] = Math.round((progressEvent.loaded * 100) / progressEvent.total);

              const sum = Object.keys(objDate).reduce((acc, name) => acc + objDate[name], 0);
              if (sum / videosInfo.length === 100 && updateDownloads !== null) {
                updateDownloads({ id: name, progress: Math.round(sum / videosInfo.length) });
              }

              if (new Date() - startDate > 300 && updateDownloads !== null) {
                startDate = new Date();
                updateDownloads({ id: name, progress: Math.round(sum / videosInfo.length) });
              }
            },
          })
          .then(res => res.data)
          .then(data => {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = url;
            link.download = name;
            link.setAttribute('download', `${name}.${type}`);

            document.body.appendChild(link);

            link.click();

            link.parentNode.removeChild(link);
          })
          .then(() => {
            updateDownloads({ id: name, progress: 100 });
            console.log(`finished ${name}`);
          });
      })
    );
  }

  static async uploadVideoToAws({ video, onUploadProgress, videoExtension = '.webm', videoIndex = 1, draftStoryId, draftSceneId }) {
    let localSrorageUser = localStorage.getItem('userDetails');
    const user = JSON.parse(localSrorageUser);

    Amplify.configure({
      Auth: {
        identityPoolId: user.identityPoolId,
        region: user.region,
        userPoolId: user.userPoolId,

        // Required if userPoolId is defined? https://github.com/aws-amplify/amplify-js/issues/267
        userPoolWebClientId: user.userPoolWebClientId,
      },
      Storage: {
        bucket: user.bucket, //REQUIRED -  Amazon S3 bucket
        region: user.region, //OPTIONAL -  Amazon service region
      },
    });

    localStorage.setItem('configured', JSON.stringify(true));

    console.log('uploadVideoToAws', Auth, Storage);
    let storyId = draftStoryId || uuidv4();
    let sceneId = draftSceneId || uuidv4();

    let filePath = storyId + '/' + sceneId + videoExtension.toLowerCase();
    console.log('filePath', filePath);

    // Make sure session is not expired 
    try {
      await Auth.currentSession(); // Will auto-refresh if expired
      await Auth.currentAuthenticatedUser(); // Ensures user context
      await Auth.currentCredentials();
    } catch (error) {
      console.error("Error refreshing session:", error);
      throw new Error("Unable to refresh session");
    }

    const uploadResult = await Storage.put(filePath, video, {
      level: 'protected',
      progressCallback({ loaded, total }) {
        const percent = Math.round((loaded / total) * 100);
        onUploadProgress({ percent, loaded, total, videoIndex });
        console.log(`Upload progress: ${percent}%`);
      },
    });

    return uploadResult.key;
  }

  static getObjectByUrl = async url => {
    let type = url
      .split(/[#?]/)[0]
      .split('.')
      .pop()
      .trim();
    if (type === 'jpg' || type === 'jpeg') {
      type = 'image/jpeg';
    } else {
      type = 'application/octet-stream';
    }
    try {
      await http.get(url, {
        headers: {
          'Content-Type': type,
        },
      });
      return true;
    } catch (e) {
      console.log('get object by url error ', e);
    }
  };

  static async getUploadUrl(videoId, payload) {
    let response = await CmsAPI.getUploadUrl(videoId, payload);
    if (response?.result) {
      return response.result;
    } else {
      return;
    }
  }

  static uploadPngByUrl = async (url, uploadId, imgFile) => {
    try {
      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'image/png',
        },
        body: imgFile,
      });
      return null;
    } catch (e) {
      console.log('upload .png error ', e);
      return uploadId;
    }
  };

  static inviteUser = async (videoId, scenesId, userEmail, message, allowReadFullStory, allowUpdateFullStory, expiresAt) => {
    const response = await VormatsAPI.inviteUser(
      videoId,
      scenesId,
      userEmail,
      message === '' ? null : message,
      allowReadFullStory,
      allowUpdateFullStory,
      expiresAt
    );
    return response;
  };

  static sendReminder = async (storyId, username) => {
    const response = await VormatsAPI.sendReminder(storyId, username);
    return response;
  };

  static getVideoAssignmentsList = async (storyId, signal = null) => {
    const response = await VormatsAPI.getVideoAssignmentsList(storyId, signal);
    return response;
  };

  static getStoryPermissions = async storyId => {
    const response = await VormatsAPI.getStoryPermissions(storyId);
    return response;
  };

  static getStoryPermissionsMe = async storyId => {
    const response = await VormatsAPI.getStoryPermissionsMe(storyId);
    return response;
  };

  static bulkUpdateAssignments = async (storyId, users) => {
    const response = await VormatsAPI.bulkUpdateAssignments(storyId, users);
    return response;
  };

  static updateUserPermissions = async (storyId, users) => {
    const response = await VormatsAPI.updateUserPermissions(storyId, users);
    return response;
  };

  static removeUserFromStory = async (storyId, username) => {
    const response = await VormatsAPI.removeUserFromStory(storyId, username);
    return response;
  };

  static getIntroOutroConfig = async (orientation, language) => {
    const response = await VormatsAPI.getIntroOutroConfig(orientation, language);
    return response;
  };

  static getAllAssignments = async () => {
    const response = await VormatsAPI.getAssignments();
    return response;
  };

  static getProjectUrl = videoId => {
    return `${config.domain}/eg/storyboard/${videoId}`;
  };

  static readAssignments = async storyId => {
    const response = await VormatsAPI.readAssignments(storyId);
    return response;
  };

  static getDefaultSubtitlesSettings = async () => {
    return VormatsAPI.getDefaultSubtitlesSettings();
  };

  static getSubtitlesSettings = async companyId => {
    return VormatsAPI.getSubtitlesSettings(companyId);
  };

  static updateSubtitlesSettings = async (companyId, settings) => {
    const response = await VormatsAPI.updateSubtitlesSettings(companyId, settings);
    return response;
  };

  static completeAssignments = async (storyId, payload) => {
    const response = await VormatsAPI.completeAssignments(storyId, payload);
    return response;
  };

  static updateUserAssignmentsCompletion = async (storyId, userId, payload) => {
    const response = await VormatsAPI.updateUserAssignmentsCompletion(storyId, userId, payload);
    return response;
  };

  static completeAssignment = async (storyId, payload) => {
    const response = await VormatsAPI.completeAssignment(storyId, payload);
    return response;
  };

  static getStoryChapters = async storyId => {
    const response = await VormatsAPI.getStoryChapters(storyId);
    return response;
  };

  static updateStoryChapters = async (storyId, chapters) => {
    const response = await VormatsAPI.updateStoryChapters(storyId, chapters);
    return response;
  };

  static getStoryCallActions = async storyId => {
    const response = await VormatsAPI.getStoryCallActions(storyId);
    return response;
  };

  static getStoryCallAction = async actionId => {
    const response = await VormatsAPI.getStoryCallAction(actionId);
    return response;
  };

  static createStoryCallAction = async action => {
    const response = await VormatsAPI.createStoryCallAction(action);
    return response;
  };

  static updateStoryCallAction = async (actionId, action) => {
    const response = await VormatsAPI.updateStoryCallAction(actionId, action);
    return response;
  };

  static removeStoryCallAction = async actionId => {
    const response = await VormatsAPI.removeStoryCallAction(actionId);
    return response;
  };

  /**
   * @typedef {Object} PresignedPost
   * @property {string} url
   * @property {Object} fields
   */

  /**
   * @param storyId
   * @returns {Promise<PresignedPost>}
   */
  static getCallActionUploadUrl = async storyId => {
    return VormatsAPI.getCallActionUploadUrl(storyId);
  };

  static uploadCallToAction = async (file, url, fields) => {
    return VormatsAPI.uploadPostForm(file, url, fields);
  };

  static getStoryboardTemplates = async (params, companyId) => {
    const response = await VormatsAPI.getStoryboardTemplates(params, companyId);
    return response;
  };

  static getStoryboardCategories = async () => {
    const response = await VormatsAPI.getStoryboardCategories();
    return response;
  };

  static createStoryboard = async payload => {
    const response = await VormatsAPI.createStoryboard(payload);
    return response;
  };

  static getStoryStoryboardRequest = async storyId => {
    const response = await VormatsAPI.getStoryStoryboardRequest(storyId);
    return response;
  };

  static getDefaultStoryboardTemplates = async language => {
    const response = await VormatsAPI.getDefaultStoryboardTemplates(language);
    return response;
  };

  static createStoryboardTemplate = async (payload, companyId) => {
    const response = await VormatsAPI.createStoryboardTemplate(payload, companyId);
    return response;
  };

  static updateStoryboardTemplate = async (templateId, payload, companyId) => {
    const response = await VormatsAPI.updateStoryboardTemplate(templateId, payload, companyId);
    return response;
  };

  static removeStoryboardTemplate = async (templateId, companyId) => {
    const response = await VormatsAPI.removeStoryboardTemplate(templateId, companyId);
    return response;
  };

  static getCompanyInfo = async companyId => {
    const response = await VormatsAPI.getCompanyInfo(companyId);
    return response;
  };

  static updateCompanyInfo = async (companyId, payload) => {
    const response = await VormatsAPI.updateCompanyInfo(companyId, payload);
    return response;
  };

  static getStoryAnalytics = async storyId => {
    const response = await VormatsAPI.getStoryAnalytics(storyId);
    return response;
  };

  static getCompanyUsers = async (params, companyId) => {
    const response = await VormatsAPI.getCompanyUsers(params, companyId);
    return response;
  };

  static getCompanyUser = async (userId, companyId) => {
    const response = await VormatsAPI.getCompanyUser(userId, companyId);
    return response;
  };

  static createCompanyUser = async (payload, companyId) => {
    const response = await VormatsAPI.createCompanyUser(payload, companyId);
    return response;
  };

  static editCompanyUser = async (userId, payload, companyId) => {
    const response = await VormatsAPI.editCompanyUser(userId, payload, companyId);
    return response;
  };

  static activateCompanyUser = async (userId, companyId) => {
    const response = await VormatsAPI.activateCompanyUser(userId, companyId);
    return response;
  };

  static deactivateCompanyUser = async (userId, companyId) => {
    const response = await VormatsAPI.deactivateCompanyUser(userId, companyId);
    return response;
  };

  static deleteCompanyUser = async (userId, companyId) => {
    const response = await VormatsAPI.deleteCompanyUser(userId, companyId);
    return response;
  };

  static resetPasswordForCompanyUser = async (userId, companyId) => {
    const response = await VormatsAPI.resetPasswordForCompanyUser(userId, companyId);
    return response;
  };

  static getUserListAsCsv = async () => {
    const response = await VormatsAPI.getUserListAsCsv();
    return response;
  };

  static resendCompanyUserInvite = async (userId, companyId) => {
    const response = await VormatsAPI.resendUserInvite(userId, companyId);
    return response;
  };

  static getUserListAsCsv = async companyId => {
    const response = await VormatsAPI.getUserListAsCsv(companyId);
    return response;
  };

  static importUsersFromCsv = async (payload, params, companyId) => {
    const response = await VormatsAPI.importUsersFromCsv(payload, params, companyId);
    return response;
  };

  static checkFileInS3 = async path => {
    const response = await CmsAPI.checkFileInS3(path);
    return response;
  };

  static getLogos = async companyId => {
    const response = await VormatsAPI.getLogos(companyId);
    return response;
  };

  static getLogo = async (logoId, companyId) => {
    const response = await VormatsAPI.getLogo(logoId, companyId);
    return response;
  };

  static uploadLogo = async (file, fileName, companyId) => {
    return VormatsAPI.uploadLogo(file, fileName, companyId);
  };

  static createLogo = async (payload, companyId) => {
    const response = await VormatsAPI.createLogo(payload, companyId);
    return response;
  };

  static updateLogo = async (logoId, logoName, payload, companyId) => {
    const response = await VormatsAPI.updateLogo(logoId, logoName, payload, companyId);
    return response;
  };

  static updateLogos = async (payload, companyId) => {
    const response = await VormatsAPI.updateLogos(payload, companyId);
    return response;
  };

  static deleteLogo = async (logoId, companyId) => {
    const response = await VormatsAPI.deleteLogo(logoId, companyId);
    return response;
  };

  static getTextPanels = async companyId => {
    const response = await VormatsAPI.getTextPanels(companyId);
    return response;
  };

  static getTextPanel = async (textPanelId, companyId) => {
    const response = await VormatsAPI.getTextPanel(textPanelId, companyId);
    return response;
  };

  static createTextPanel = async (payload, companyId) => {
    const response = await VormatsAPI.createTextPanel(payload, companyId);
    return response;
  };

  static updateTextPanel = async (textPanelId, payload, companyId) => {
    const response = await VormatsAPI.updateTextPanel(textPanelId, payload, companyId);
    return response;
  };

  static deleteTextPanel = async (textPanelId, companyId) => {
    const response = await VormatsAPI.deleteTextPanel(textPanelId, companyId);
    return response;
  };

  static getCompaniesList = async (id, payload) => {
    const response = await VormatsAPI.getCompaniesList(id, payload);
    return response;
  };

  static getFontsListForManagement = async params => {
    const response = await VormatsAPI.getFontsListForManagement(params);
    return response;
  };

  static getFontsList = async companyId => {
    return VormatsAPI.getFontsList(companyId);
  };

  static getFontById = async id => {
    const response = await VormatsAPI.getFontById(id);
    return response;
  };

  static uploadFont = async file => {
    return VormatsAPI.uploadFont(file);
  };

  static createFont = async payload => {
    const response = await VormatsAPI.createFont(payload);
    return response;
  };

  static updateFont = async (id, payload) => {
    const response = await VormatsAPI.updateFont(id, payload);
    return response;
  };

  static deleteFont = async id => {
    const response = await VormatsAPI.deleteFont(id);
    return response;
  };

  /**
   * @param {number} companyId
   */
  static getCompanyUsages = async companyId => {
    return VormatsAPI.getCompanyUsages(companyId);
  };

  /**
   * @param {number} companyId
   * @param {UpdateUsagePayload} payload
   */
  static createCompanyUsage = async (companyId, payload) => {
    return VormatsAPI.createCompanyUsage(companyId, payload);
  };

  /**
   * @param {string} usageId
   * @param {UpdateUsagePayload} payload
   */
  static updateCompanyUsage = async (usageId, payload) => {
    return VormatsAPI.updateCompanyUsage(usageId, payload);
  };

  static createFromTemporaryStory = async id => {
    return VormatsAPI.createFromTemporaryStory(id);
  };

  static getUsagePerUser = async (companyId, contractId, params) => {
    return VormatsAPI.getUsagePerUser(companyId, contractId, params);
  };

  static uploadVoiceover = async (file, url, fields) => {
    return VormatsAPI.uploadPostForm(file, url, fields);
  };

  static async getStoryGeneralVoiceovers(storyId) {
    return VormatsAPI.getStoryGeneralVoiceovers(storyId);
  }

  static async createStoryGeneralVoiceovers(storyId) {
    return VormatsAPI.createStoryGeneralVoiceovers(storyId);
  }

  static async getVoiceoverUploadUrl(storyId, trackId) {
    return VormatsAPI.getVoiceoverUploadUrl(storyId, trackId);
  }

  static async getStoryVoiceovers(storyId, trackId) {
    return VormatsAPI.getStoryVoiceovers(storyId, trackId);
  }

  static async updateStoryVoiceovers(storyId, trackId, payload) {
    return VormatsAPI.updateStoryVoiceovers(storyId, trackId, payload);
  }

  static async applyStoryVoiceovers(storyId) {
    return CmsAPI.applyStoryVoiceovers(storyId);
  }

  static async getStoryVoiceoverTrack(storyId) {
    return VormatsAPI.getStoryVoiceoverTrack(storyId);
  }

  static async enhanceSceneAudio(storyId, sceneId) {
    return VormatsAPI.enhanceSceneAudio(storyId, sceneId);
  }

  static async removeEnhanceScene(storyId, sceneId) {
    return VormatsAPI.removeEnhanceScene(storyId, sceneId);
  }

  static async uploadExternalEditedVideo(storyId, file, onUploadProgress) {
    return VormatsAPI.uploadExternalEditedVideo(storyId, file, onUploadProgress);
  }

  static async createExternalVideoVersion(storyId, name, fileUrl) {
    return VormatsAPI.createExternalVideoVersion(storyId, name, fileUrl);
  }

  static async deleteExternalVideoVersion(storyId, resourceId) {
    return VormatsAPI.deleteExternalVideoVersion(storyId, resourceId);
  }

  static async getDownloadSceneUrl(storyId, sceneId) {
    return VormatsAPI.getDownloadSceneUrl(storyId, sceneId);
  }

  static async generateAvatarClip(storyId, sceneId, avatarId, voiceId) {
    return VormatsAPI.generateAvatarClip(storyId, sceneId, avatarId, voiceId);
  }

  static async getStoryConfig(storyId) {
    return VormatsAPI.getStoryConfig(storyId);
  }

  static async generateBatchAvatarClips(storyId, payload) {
    return VormatsAPI.generateBatchAvatarClips(storyId, payload);
  }

  static async generateStoryAvatars(storyId, sceneIds) {
    return VormatsAPI.generateStoryAvatars(storyId, sceneIds);
  }

  static async getAvatarsTemplates() {
    return VormatsAPI.getAvatarsTemplates();
  }

  static async getStorySchedule(videoId) {
    return VormatsAPI.getStorySchedule(videoId);
  }

  static async createStorySchedule(storyId, payload) {
    return VormatsAPI.createStorySchedule(storyId, payload);
  }

  static async updateStorySchedule(storyId, payload) {
    return VormatsAPI.updateStorySchedule(storyId, payload);
  }

  static async removeSchedule(storyId) {
    return VormatsAPI.removeSchedule(storyId);
  }

  static async updateScheduleNotifications(storyId, notifications) {
    return VormatsAPI.updateScheduleNotifications(storyId, notifications);
  }

  static async getMySchedule() {
    return VormatsAPI.getMySchedule();
  }

  static async getStoriesList(query) {
    return VormatsAPI.getStoriesList(query);
  }

  static async getUserCalendarIcs() {
    return VormatsAPI.getUserCalendarIcs();
  }

  static async generateVideoForStory(storyId, templateId = null) {
    return VormatsAPI.generateVideoForStory(storyId, templateId);
  }

  static async generateAvatarVoicePreview(payload) {
    return VormatsAPI.generateAvatarVoicePreview(payload);
  }

  static async saveScenesAvatarMetadata(storyId, payload) {
    return VormatsAPI.saveScenesAvatarMetadata(storyId, payload);
  }

  static async getScenesAvatarMetadata(storyId) {
    return VormatsAPI.getScenesAvatarMetadata(storyId);
  }

  static async deleteScenesAvatarMetadata(storyId, sceneIds) {
    return VormatsAPI.deleteScenesAvatarMetadata(storyId, sceneIds);
  }
}
